import { Dialog, Transition } from "@headlessui/react";
import {
  LocationMarkerIcon,
  PlusIcon,
  TrashIcon,
  XIcon
} from "@heroicons/react/outline";
import { PencilIcon } from "@heroicons/react/solid";
import { Field, FormikHelpers, FormikProps } from "formik";
import { every, get, isEmpty, toLower, values, without, words } from "lodash";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import * as Yup from "yup";
import DropdownSearch from "../../../components/forms/DropdownSearch";
import FormProvider from "../../../components/forms/FormProvider";
import Input, {
  defaultPhoneCountryCode
} from "../../../components/forms/Input";
import { useContacts } from "../../../context/contacts";
import { useLocations } from "../../../context/locations";
import {
  isHotspotLocation,
  LocationType
} from "../../../context/locations/LocationFormSidebar";
import classNames from "../../../utils/classNames";
import { Contact, HotspotLocation, PaymentMethod } from "../../../utils/types";
import usePrevious from "../../../utils/usePrevious";
import PaymentMethodComponent from "../common/PaymentMethodComponent";
import PaymentMethodFormSidebar, {
  EditPaymentMethodConfig
} from "./PaymentMethodFormSidebar";

interface Props {
  contact?: Contact | undefined;
  open: boolean;
  toggleEditContactForm: (contact: Contact | undefined) => void;
  onSuccess?: (contact: Contact) => void;
  showLinkedLocations?: boolean;
}

const ContactFormSidebar: React.FC<Props> = ({
  contact,
  open,
  showLinkedLocations = true,
  toggleEditContactForm
}) => {
  const { addContact, updateContact, removePaymentMethod } = useContacts();
  const {
    locations,
    fetchLocations,
    setEditLocationConfig,
    editLocationConfig
  } = useLocations();
  const [linkedLocationUuids, setLinkedLocationUuids] = useState<string[]>([]);
  const [linkedPaymentMethods, setLinkedPaymentMethods] = useState<
    PaymentMethod[]
  >([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const dialogRef = useRef(null);
  const [editPaymentMethodConfig, setEditPaymentMethodConfig] =
    useState<EditPaymentMethodConfig>({ show: false });

  const prevContact = usePrevious(contact);
  useEffect(() => {
    if (contact && contact !== prevContact) {
      setLinkedLocationUuids(contact?.linked_location_ids || []);
      setLinkedPaymentMethods(contact?.payment_methods || []);
    }
  }, [contact, prevContact]);

  const closeSidebar = useCallback(
    (contact?: Contact) => {
      toggleEditContactForm(contact);
      setLinkedLocationUuids([]);
      setLinkedPaymentMethods([]);
    },
    [toggleEditContactForm]
  );

  const initialValues = useMemo(
    () =>
      contact
        ? {
            name: contact.name,
            email: contact.email || "",
            phone: contact.phone || "",
            vat: contact.vat || ""
          }
        : {
            name: "",
            email: "",
            phone: "",
            vat: ""
          },
    [contact]
  );

  const onSubmit = useCallback(
    async (
      { name, email, phone, vat }: FormValues,
      formikHelpers: FormikHelpers<FormValues>
    ) => {
      setErrorMessage(null);

      const contactToSave = {
        name,
        email: email || null,
        phone: phone || null,
        payment_methods: linkedPaymentMethods,
        locations_ids: linkedLocationUuids,
        vat: vat
      };

      let apiResponse;

      if (contact?.uuid) {
        apiResponse = await updateContact(contact.uuid, contactToSave);
      } else {
        apiResponse = await addContact(contactToSave);
      }

      if (
        !isEmpty(linkedLocationUuids) ||
        (isEmpty(linkedLocationUuids) &&
          !isEmpty(get(contact, "linked_location_ids", [])))
      ) {
        await fetchLocations();
      }

      if (apiResponse?.status) {
        closeSidebar(apiResponse.data);
      } else {
        setErrorMessage("Something went wrong");
      }

      formikHelpers.setSubmitting(false);
    },
    [
      addContact,
      closeSidebar,
      contact,
      fetchLocations,
      linkedLocationUuids,
      linkedPaymentMethods,
      updateContact
    ]
  );

  const getLocations = useCallback(
    (searchText: string | undefined) => {
      const filteredLocations = values(locations).filter((location) => {
        if (linkedLocationUuids.includes(location.uuid)) {
          return false;
        }

        if (searchText) {
          const searchInputWords = words(searchText, /[^, ]+/g).map(toLower);

          return every(
            searchInputWords,
            (word) =>
              toLower(location.name).includes(word) ||
              toLower(location.address || "").includes(word)
          );
        }
        return true;
      });

      return filteredLocations.slice(0, 10);
    },
    [locations, linkedLocationUuids]
  );

  const hasParentForm = useMemo(
    () =>
      (editLocationConfig?.parentForm === "contact" &&
        editLocationConfig?.show) ||
      (editPaymentMethodConfig?.parentForm === "contact" &&
        editPaymentMethodConfig?.show),
    [
      editLocationConfig?.parentForm,
      editLocationConfig?.show,
      editPaymentMethodConfig?.parentForm,
      editPaymentMethodConfig?.show
    ]
  );

  const checkCanShow = useCallback(() => {
    if (editPaymentMethodConfig.show || editLocationConfig?.show) return false;
    return open;
  }, [open, editPaymentMethodConfig, editLocationConfig]);

  const handlePaymentMethods = useCallback(
    (
      paymentMethod: PaymentMethod,
      action: "add" | "edit",
      paymentIndex: number
    ) => {
      setLinkedPaymentMethods((prevState) => {
        const currentDefaultMethod = prevState.filter(
          ({ primary, currency }) =>
            primary === true && currency === paymentMethod.currency
        )[0];

        let result = [...prevState];

        if (paymentMethod.primary && currentDefaultMethod) {
          const indexOfCurrentDefaultMethod =
            prevState.indexOf(currentDefaultMethod);

          result[indexOfCurrentDefaultMethod].primary = false;

          if (action !== "edit") {
            return [...result, paymentMethod];
          }
        }

        if (!currentDefaultMethod) paymentMethod.primary = true;

        if (action === "edit") {
          result[paymentIndex] = paymentMethod;
          return [...result];
        }

        return [...prevState, paymentMethod];
      });
    },
    []
  );

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          static
          className="fixed inset-0 z-20 overflow-hidden"
          open={checkCanShow()}
          onClose={() => {
            if (hasParentForm) return;

            closeSidebar();
          }}
          initialFocus={dialogRef}
        >
          <PaymentMethodFormSidebar
            config={editPaymentMethodConfig}
            setConfig={setEditPaymentMethodConfig}
          />
          <div ref={dialogRef} className="absolute inset-0 overflow-hidden">
            <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

            <div
              className={classNames(
                "fixed inset-y-0 pl-16 max-w-full right-0 flex duration-500",
                hasParentForm && "pr-40"
              )}
            >
              <div
                className="absolute inset-0"
                onClick={() => closeSidebar()}
              />
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <div className="relative w-screen max-w-full sm:max-w-md">
                  <FormProvider
                    classnames={{ wrapper: "h-full" }}
                    onCancel={() => closeSidebar()}
                    error={errorMessage}
                    formikProps={{
                      onSubmit,
                      initialValues,
                      validationSchema: Yup.object().shape({
                        name: Yup.string()
                          .min(3, "Please enter at least 3 characters")
                          .required("Required"),
                        email: Yup.string()
                          .email("Please enter a valid email address")
                          .nullable(),
                        phone: Yup.string().test(
                          "validPhone",
                          "Please enter a valid phone number.",
                          (value) =>
                            value === undefined
                              ? true
                              : isPossiblePhoneNumber(value)
                        )
                      })
                    }}
                    orderedFieldNames={ORDERED_FIELD_NAMES}
                  >
                    {({ isSubmitting }: FormikProps<FormValues>) => {
                      return (
                        <div className="flex-1 h-0 overflow-y-auto">
                          <div className="py-6 px-4 bg-hotspotty-700 sm:px-6 dark:bg-gray-800">
                            <div className="flex items-center justify-between">
                              <Dialog.Title className="text-lg font-medium text-white">
                                {!!contact ? "Edit" : "New"} contact
                              </Dialog.Title>
                              <div className="ml-3 h-7 flex items-center">
                                <button
                                  type="button"
                                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-800 dark:hover:text-white"
                                  onClick={() => closeSidebar()}
                                >
                                  <span className="sr-only">Close panel</span>
                                  <XIcon
                                    className="h-6 w-6"
                                    aria-hidden="true"
                                  />
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="flex-1 flex flex-col justify-between">
                            <div className="px-4 divide-y divide-gray-200 dark:divide-gray-500 sm:px-6">
                              <div className="space-y-6 pt-6 pb-5">
                                <Field
                                  id="name"
                                  name="name"
                                  label="Name"
                                  placeholder="John Doe"
                                  type="text"
                                  component={Input}
                                  disabled={isSubmitting}
                                />

                                <Field
                                  id="email"
                                  name="email"
                                  label="Email"
                                  placeholder="john@acme.com"
                                  type="email"
                                  component={Input}
                                  disabled={isSubmitting}
                                />

                                <Field
                                  id="phone"
                                  name="phone"
                                  label="Phone"
                                  placeholder="+35191483792"
                                  type="text"
                                  isPhone
                                  defaultCountry={defaultPhoneCountryCode}
                                  component={Input}
                                  disabled={isSubmitting}
                                />

                                <Field
                                  id="vat"
                                  name="vat"
                                  label="Tax ID"
                                  placeholder="XXXXXXXXXXXX"
                                  type="text"
                                  component={Input}
                                  disabled={isSubmitting}
                                />

                                {showLinkedLocations && (
                                  <>
                                    <Field
                                      label={
                                        <div className="flex items-center justify-between">
                                          <div>Locations</div>
                                          <div
                                            onClick={() => {
                                              setEditLocationConfig({
                                                show: true,
                                                parentForm: "contact",
                                                onSuccess: (
                                                  location: LocationType
                                                ) => {
                                                  if (
                                                    isHotspotLocation(
                                                      location
                                                    ) &&
                                                    !linkedLocationUuids.includes(
                                                      location.uuid
                                                    )
                                                  ) {
                                                    setLinkedLocationUuids(
                                                      linkedLocationUuids.concat(
                                                        location.uuid
                                                      )
                                                    );
                                                  }
                                                }
                                              });
                                            }}
                                            className="flex items-center text-sm text-gray-500 dark:text-white cursor-pointer"
                                          >
                                            <PlusIcon className="h-4 w-4 ml-1 text-gray-500 dark:text-white" />
                                            Add new location
                                          </div>
                                        </div>
                                      }
                                      name="selectedLinkedLocation"
                                      placeholder={
                                        isEmpty(locations)
                                          ? "No locations available"
                                          : "Search locations"
                                      }
                                      disabled={
                                        isSubmitting || isEmpty(locations)
                                      }
                                      fetchItems={getLocations}
                                      itemToString={(
                                        location: HotspotLocation
                                      ) => location.name}
                                      component={DropdownSearch}
                                      withoutMinimumSearchCharacters
                                      initialItems={getLocations("")}
                                      onSelectedItem={(
                                        location: HotspotLocation | undefined,
                                        clearInputCallback: () => void
                                      ) => {
                                        if (location) {
                                          setLinkedLocationUuids(
                                            (prevLinkedLocationUuids) =>
                                              prevLinkedLocationUuids.concat(
                                                location.uuid
                                              )
                                          );

                                          clearInputCallback();
                                        }
                                      }}
                                    />

                                    {!isEmpty(linkedLocationUuids) && (
                                      <div className="flex flex-col w-full shadow overflow-hidden sm:rounded-lg">
                                        <div className="bg-white divide-y divide-gray-200 text-gray-500 dark:text-gray-50 dark:divide-gray-500 dark:bg-gray-800">
                                          {linkedLocationUuids.map((uuid) => (
                                            <div
                                              key={uuid}
                                              className="px-6 py-4 whitespace-nowraprelative flex items-center space-x-3 group"
                                            >
                                              <LocationMarkerIcon className="flex-shrink-0 h-6 w-6" />

                                              <div className="flex-1 min-w-0 focus:outline-none">
                                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                  {locations[uuid]?.name}
                                                </p>
                                              </div>

                                              <button
                                                className="inline-flex rounded-md p-1.5 focus:outline-none text-hotspotty-400 hover:bg-hotspotty-100 dark:text-white dark:hover:text-gray-800 disabled:opacity-50 disabled:cursor-default"
                                                type="button"
                                                disabled={isSubmitting}
                                                onClick={() => {
                                                  setLinkedLocationUuids(
                                                    (prevLinkedLocationUuids) =>
                                                      without(
                                                        prevLinkedLocationUuids,
                                                        uuid
                                                      )
                                                  );
                                                }}
                                              >
                                                <TrashIcon
                                                  className="h-5 w-5"
                                                  aria-hidden="true"
                                                />
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </>
                                )}

                                <div>
                                  <div className="mb-4 flex items-center justify-between">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                      Payment methods
                                    </p>
                                    <div
                                      onClick={() => {
                                        setEditPaymentMethodConfig({
                                          show: true,
                                          parentForm: "contact",
                                          contactLinkedUuid: contact?.uuid,
                                          linkedPaymentMethods:
                                            linkedPaymentMethods,
                                          onSuccess: async (
                                            paymentMethod: PaymentMethod
                                          ) => {
                                            handlePaymentMethods(
                                              paymentMethod,
                                              "add",
                                              0
                                            );
                                          }
                                        });
                                      }}
                                      className="flex items-center text-sm text-gray-500 font-medium dark:text-white cursor-pointer"
                                    >
                                      <PlusIcon className="h-4 w-4 ml-1 text-gray-500 dark:text-white" />
                                      Add payment method
                                    </div>
                                  </div>

                                  {isEmpty(linkedPaymentMethods) ? (
                                    <div className="px-6 py-4 bg-white divide-y divide-gray-200 text-sm font-medium text-gray-500 shadow overflow-hidden sm:rounded-lg dark:text-gray-50 dark:divide-gray-500 dark:bg-gray-800">
                                      No payment methods were added yet
                                    </div>
                                  ) : (
                                    <div className="bg-white divide-y divide-gray-200 text-gray-500 dark:text-gray-50 dark:divide-gray-500 dark:bg-gray-800 sm:rounded-lg">
                                      {linkedPaymentMethods.map(
                                        (paymentMethod, paymentIndex) => (
                                          <div
                                            key={paymentIndex}
                                            className="px-6 py-4 whitespace-nowraprelative flex items-center space-x-3 group"
                                          >
                                            <div className="flex-1 flex-col min-w-0 focus:outline-none">
                                              <PaymentMethodComponent
                                                paymentMethod={paymentMethod}
                                              />
                                            </div>
                                            <button
                                              className="inline-flex rounded-md p-1.5 focus:outline-none text-hotspotty-400 hover:bg-hotspotty-100 dark:text-white dark:hover:text-gray-800 disabled:opacity-50 disabled:cursor-default"
                                              type="button"
                                              disabled={isSubmitting}
                                              onClick={() => {
                                                setEditPaymentMethodConfig({
                                                  show: true,
                                                  contactLinkedUuid:
                                                    contact?.uuid,
                                                  parentForm: "contact",
                                                  paymentMethod,
                                                  onSuccess: async (
                                                    paymentMethod: PaymentMethod
                                                  ) => {
                                                    handlePaymentMethods(
                                                      paymentMethod,
                                                      "edit",
                                                      paymentIndex
                                                    );
                                                  }
                                                });
                                              }}
                                            >
                                              <PencilIcon
                                                className="h-5 w-5"
                                                aria-hidden="true"
                                              />
                                            </button>
                                            <button
                                              style={{ marginLeft: 0 }}
                                              className="inline-flex rounded-md p-1.5 focus:outline-none text-hotspotty-400 hover:bg-hotspotty-100 dark:text-white dark:hover:text-gray-800 disabled:opacity-50 disabled:cursor-default"
                                              type="button"
                                              disabled={isSubmitting}
                                              onClick={() => {
                                                setLinkedPaymentMethods(
                                                  (prevState) =>
                                                    prevState.filter(
                                                      (_item, index) =>
                                                        index !== paymentIndex
                                                    )
                                                );

                                                if (
                                                  contact?.uuid &&
                                                  paymentMethod.uuid
                                                ) {
                                                  removePaymentMethod(
                                                    contact?.uuid,
                                                    paymentMethod.uuid
                                                  );
                                                }
                                              }}
                                            >
                                              <TrashIcon
                                                className="h-5 w-5"
                                                aria-hidden="true"
                                              />
                                            </button>
                                          </div>
                                        )
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  </FormProvider>
                </div>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default ContactFormSidebar;

//
// Utils
//

interface FormValues {
  name: string;
  email: string;
  phone: string;
  vat: string;
}

const ORDERED_FIELD_NAMES = ["name", "email", "phone", "vat"];
